/**
 * The whole manuscript as one Markdown file (#258), in the same order and
 * with the same front matter as the PDF / EPUB / DOCX builds: title page,
 * copyright, dedication, then each chapter with its scenes.
 *
 * Headings: `#` book title, `##` chapters, `###` scene titles (when the
 * project shows them). Scene breaks follow `sceneFormatting`. Comments are
 * omitted; alignment and indent are dropped (see `zwdocToMarkdown`).
 */

import type { Node as PMNode } from "prosemirror-model";
import { atIndexMaybe, isJust, just, type Maybe } from "../lib/maybe";
import {
  defaultZwCopyrightPage,
  defaultZwDedicationPage,
  defaultZwMetadata,
  sceneDisplayName,
  type ZwChapter,
  type ZwCopyrightPage,
  type ZwDedicationPage,
  type ZwMetadata,
  type ZwSceneFormatting,
} from "../lib/zwConfig";
import { buildCopyrightPageHtml } from "./copyrightPage";
import { buildDedicationPageHtml } from "./dedicationPage";
import { sceneBreakMarkerText } from "./sceneFormatting";
import { zwdocToMarkdown } from "./zwdocMarkdown";

export type MarkdownBookChapter = {
  chapter: ZwChapter;
  /** Scene documents in manuscript order. */
  scenes: readonly PMNode[];
};

export type BuildMarkdownInput = {
  title: string;
  subtitle: string;
  author: string;
  copyrightPage?: ZwCopyrightPage;
  dedicationPage?: ZwDedicationPage;
  metadata?: ZwMetadata;
  sceneFormatting: ZwSceneFormatting;
  chapters: readonly MarkdownBookChapter[];
};

/** Plain text that can't be read as Markdown syntax (titles, names). */
function escapeText(text: string): string {
  return text
    .replace(/[\\`*_[\]<>#~=|]/g, "\\$&")
    .replace(/\s+/g, " ")
    .trim();
}

/** Paragraph texts of a front-matter page built as HTML for the other targets. */
function pageParagraphs(html: Maybe<string>): string[] {
  if (isJust(html) === false) return [];
  const parsed = new DOMParser().parseFromString(html.value, "text/html");
  return Array.from(parsed.querySelectorAll("p"), (p) =>
    escapeText(p.textContent ?? ""),
  ).filter((text) => text !== "");
}

function sceneBreak(formatting: ZwSceneFormatting): Maybe<string> {
  const marker = sceneBreakMarkerText(formatting);
  if (isJust(marker) === false) return marker;
  // "* * *" is already a Markdown thematic break; anything else is text.
  if (marker.value === "* * *") return marker;
  return just(marker.value === "" ? "&nbsp;" : escapeText(marker.value));
}

function chapterMarkdown(
  { chapter, scenes }: MarkdownBookChapter,
  formatting: ZwSceneFormatting,
): string {
  const parts = [
    `## ${chapter.name.split("\n").map(escapeText).join("<br />")}`,
  ];
  const bodies = scenes.map((doc) => zwdocToMarkdown(doc).trim());
  // Like the other targets: no scene hierarchy until a chapter has 2+ scenes.
  if (bodies.length <= 1) {
    parts.push(...bodies.filter((body) => body !== ""));
    return parts.join("\n\n");
  }
  const marker = sceneBreak(formatting);
  bodies.forEach((body, index) => {
    if (index > 0 && isJust(marker)) parts.push(marker.value);
    const scene = atIndexMaybe(chapter.scenes, index);
    if (formatting.showSceneTitles && isJust(scene)) {
      parts.push(`### ${escapeText(sceneDisplayName(scene.value, index))}`);
    }
    if (body !== "") parts.push(body);
  });
  return parts.join("\n\n");
}

export function buildBookMarkdown(input: BuildMarkdownInput): string {
  const title = input.title.trim();
  const author = input.author.trim();
  const subtitle = input.subtitle.trim();
  const metadata = {
    ...defaultZwMetadata(),
    ...input.metadata,
    title,
    subtitle,
    author,
  };

  const front: string[] = [`# ${escapeText(title)}`];
  if (subtitle !== "") front.push(`*${escapeText(subtitle)}*`);
  if (author !== "") front.push(`By ${escapeText(author)}`);

  const copyright = pageParagraphs(
    buildCopyrightPageHtml(
      input.copyrightPage ?? defaultZwCopyrightPage(),
      metadata,
    ),
  );
  const dedication = pageParagraphs(
    buildDedicationPageHtml(input.dedicationPage ?? defaultZwDedicationPage()),
  ).map((text) => `*${text}*`);

  // Front-matter pages are set apart by rules; chapters by their headings.
  const pages = [front.join("\n\n")];
  if (copyright.length > 0) pages.push(copyright.join("\n\n"));
  if (dedication.length > 0) pages.push(dedication.join("\n\n"));
  const chapters = input.chapters.map((chapter) =>
    chapterMarkdown(chapter, input.sceneFormatting),
  );
  return `${[...pages, chapters.join("\n\n")].join("\n\n---\n\n")}\n`;
}
